import {type Moment} from 'moment';
import React from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import {
	OldTimeRegistrationTargetSuggestions,
	TimeRegistrationTargetSuggestions,
	TimeRegistrationTargetSuggstionsData,
} from '../../../time-registration-target-suggestions/TimeRegistrationTargetSuggestionsFetch';
import {TIME_PERIOD, type TimePeriod} from '../../../timesheets_change_date_view';
import {formatForDayView} from '../timesheet-table-util/TimeRegSuggestionUtil';
import {TimesheetsTimeRowTable as TimesheetTable} from '../TimesheetsTimeRowTable';
import {useSuggestions} from './useSuggestions';
import {sortList, groupRegs, addTimeRegTypes} from '../timesheet-table-util/DataUtil';
import {ApprovalStatus} from '../../../../../../constants';
import type {TimesheetColumnSort} from '../../usePersistentTimesheetColumnSort';
import type {TimesheetTimeTableWrapper_viewer$data as TimesheetTimeTableWrapper_viewer} from './__generated__/TimesheetTimeTableWrapper_viewer.graphql';
import {useTimesheetsTaskTableColumns} from './useTimesheetsTaskTableColumns';

type TimesheetTimeTableWrapperProps = {
	viewer: TimesheetTimeTableWrapper_viewer;
	selectedPerson; // TimesheetsPage_viewer['company']['person']
	timeRegistrations; // TimesheetsPage_viewer['timeRegistrations']['edges']
	timeRegsForPeriod; // TimesheetsPage_viewer['timeRegistrations']['edges']
	currentViewingDate: Moment;
	timePeriod: TimePeriod;
	sort: TimesheetColumnSort;
	selectDate: (day: Moment) => void;
	holidays; // TimesheetsPage_viewer['company']['person']['holidayCalendar']['holidayCalendarEntries']['edges']
	lockedDate?: Moment;
	isFullyLocked: boolean;
	usingTimeApproval: boolean;
	usingInternalTimeApproval: boolean;
	periodTimeApprovalStatus: ApprovalStatus | null;
	overBudgetProgramsByCompanyProjectId; // Custom type of {companyProjectId, program}
	timeRegistrationTargetSuggestionsData: TimeRegistrationTargetSuggstionsData;
	validTimeRegistrationSuggestions: TimeRegistrationTargetSuggestions | OldTimeRegistrationTargetSuggestions;
	goToTimesheetsTab: () => void;
	showSuggestions: boolean;
	hasSuggestions: boolean;
	refetchEntries: () => void;
	customFieldDefinitions; // TimesheetsPage_viewer['company']['customFieldDefinitions]['edges'][0]['node'];
};

const TimesheetTimeTableWrapper = ({
	viewer,
	selectedPerson,
	timeRegistrations,
	timeRegsForPeriod,
	currentViewingDate,
	timePeriod,
	sort,
	selectDate,
	holidays,
	lockedDate,
	isFullyLocked,
	usingTimeApproval,
	usingInternalTimeApproval,
	periodTimeApprovalStatus,
	overBudgetProgramsByCompanyProjectId,
	timeRegistrationTargetSuggestionsData,
	validTimeRegistrationSuggestions,
	goToTimesheetsTab,
	showSuggestions,
	hasSuggestions,
	refetchEntries,
	customFieldDefinitions,
}: TimesheetTimeTableWrapperProps) => {
	const isWeekView = timePeriod === TIME_PERIOD.WEEK_VIEW;

	const columns = useTimesheetsTaskTableColumns({
		viewer,
		selectedPerson,
		isWeekView,
		customFieldDefinitions,
	});

	const {timeRegistrationsWithoutMovedSuggestions, suggestionEntitiesWithTimeRegs} = useSuggestions({
		suggestions: validTimeRegistrationSuggestions,
		timeRegistrations: timeRegsForPeriod,
		currentViewingDate,
		isWeekView,
	});

	const timeRegsToShow = showSuggestions ? timeRegistrationsWithoutMovedSuggestions : timeRegsForPeriod;
	const suggestionsToShow = showSuggestions ? suggestionEntitiesWithTimeRegs : new Map();

	const suggestionEntitiesFormattedForDayView = formatForDayView(suggestionsToShow);

	// TODO: In an effort to standardise the timesheet table we are in the process of refactoring
	// it to take a single list of row entities, rather than an array or map, of edges or nodes.
	// It's likely that much of this complexity will be refactored away.
	const timeRegsWithTypes = addTimeRegTypes(timeRegsToShow);

	let standardisedEntityList;
	if (isWeekView) {
		const entityTimeRegMap = groupRegs(timeRegsWithTypes);
		standardisedEntityList = [...suggestionsToShow.entries(), ...entityTimeRegMap.entries()].map(
			([entity, dayRegsForEntity]) => ({
				...entity,
				dayRegs: dayRegsForEntity,
			})
		);
	} else {
		standardisedEntityList = [...suggestionEntitiesFormattedForDayView, ...timeRegsWithTypes].filter(
			e => !(e.node && e.node.task && e.node.task.project === null)
		);
	}

	const sortedList = sortList(standardisedEntityList, sort.value, sort.ascending);

	return (
		<TimesheetTable
			entityList={sortedList}
			sort={sort}
			loading={false}
			viewer={viewer}
			selectedPerson={selectedPerson}
			columns={columns}
			timeRegistrations={timeRegistrations}
			currentViewingDate={currentViewingDate}
			timePeriod={timePeriod}
			searchActive={false}
			selectDate={selectDate}
			holidays={holidays}
			lockedDate={lockedDate}
			isFullyLocked={isFullyLocked}
			usingTimeApproval={usingTimeApproval}
			usingInternalTimeApproval={usingInternalTimeApproval}
			periodTimeApprovalStatus={periodTimeApprovalStatus}
			overBudgetProgramsByCompanyProjectId={overBudgetProgramsByCompanyProjectId}
			timeRegistrationTargetSuggestionsData={timeRegistrationTargetSuggestionsData}
			goToTimesheetsTab={goToTimesheetsTab}
			thisWeekWork={false}
			showSuggestions={showSuggestions}
			hasSuggestions={hasSuggestions}
			refetchEntries={refetchEntries}
		/>
	);
};

const TimesheetTimeTableWrapperQuery = graphql`
	query TimesheetTimeTableWrapper_Query {
		viewer {
			actualPersonId
			component(name: "TimesheetTimeTableWrapper")
			...TimesheetTimeTableWrapper_viewer
		}
	}
`;

export {TimesheetTimeTableWrapperQuery};

export default createFragmentContainer(TimesheetTimeTableWrapper, {
	viewer: graphql`
		fragment TimesheetTimeTableWrapper_viewer on Viewer {
			id
			actualPersonId
			harvestUser
			unit4User
			company {
				id
				bambooHREnabled
				hiBobEnabled
				harvestEnabled
				unit4Enabled
				sageIntacctEnabled
				useTimeOffApproval
				roles(includeDisabled: false) {
					edges {
						node {
							id
							name
						}
					}
				}
			}
		}
	`,
});
